import { useMemo } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import { useSeo } from "@/hooks/useSeo";
import { principles, site, stats, testimonials } from "@/mocks/site";

export default function About() {
  const baseUrl = `${window.location.origin}${__BASE_PATH__.replace(/\/$/, "")}`;

  const jsonLd = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于我们 - 瓜网",
        url: `${baseUrl}/about`,
        inLanguage: "zh-CN",
        description: site.intro,
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: site.name,
        url: baseUrl,
        email: site.email,
        description: site.intro,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${baseUrl}/` },
          { "@type": "ListItem", position: 2, name: "关于我们", item: `${baseUrl}/about` },
        ],
      },
    ],
    [baseUrl],
  );

  useSeo({
    title: "关于我们 - 瓜网 | 娱乐资讯的内容原则与团队定位",
    description:
      "了解瓜网的品牌定位、内容原则与编辑标准。我们坚持可追溯、可验证、不伤害、有增量四条准则，专注整理明星动态、影视综艺与流行文化话题。",
    keywords: "关于瓜网,瓜网团队,内容原则,娱乐资讯门户,编辑标准",
    path: "/about",
    image: testimonials[0].avatar,
    jsonLd,
  });

  return (
    <div id="top" className="min-h-screen bg-background-50">
      <SiteHeader />

      <main>
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1400px] px-4 py-12 md:px-6 md:py-16">
            <nav aria-label="面包屑" className="mb-5 flex items-center gap-2 text-xs text-foreground-500">
              <Link to="/" className="hover:text-primary-700">首页</Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
              <span className="text-foreground-700">关于我们</span>
            </nav>
            <p className="mb-3 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.24em] text-primary-700">
              <span className="inline-block h-2 w-2 rounded-full bg-accent-500"></span>
              About {site.nameEn}
            </p>
            <h1 className="max-w-3xl font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-[2.8rem] md:leading-[1.15]">
              我们只做一件事：把娱乐热点整理清楚
            </h1>
            <p className="mt-4 max-w-3xl text-base leading-relaxed text-foreground-700">{site.intro}</p>
          </div>
        </section>

        <section className="border-b border-background-200 bg-background-50" aria-label="品牌介绍">
          <div className="mx-auto grid w-full max-w-[1400px] grid-cols-1 gap-10 px-4 py-12 md:px-6 md:py-16 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-5">
              <div className="h-72 w-full overflow-hidden rounded-lg md:h-96">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20artistic%20flat%20illustration%20of%20a%20reading%20desk%20with%20open%20magazines%2C%20amber%20lamp%20glow%20and%20olive%20green%20plants%2C%20minimal%20editorial%20style%2C%20cream%20paper%20texture%2C%20no%20text&width=900&height=1100&seq=gw-about-editorial&orientation=portrait"
                  alt="瓜网编辑部日常整理娱乐资讯的工作场景插画"
                  title="瓜网编辑部 · 娱乐资讯整理"
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>
            <div className="lg:col-span-7">
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">网站定位</h2>
              <div className="mt-4 space-y-4 text-base leading-[1.9] text-foreground-800">
                <p>
                  瓜网不是一个追热点的速度型媒体。我们更在意的是：一件事发生之后，读者能不能在五分钟内看懂它的来龙去脉。因此每条内容都会补上背景、时间线和必要的判断依据。
                </p>
                <p>
                  我们服务的是普通读者，而不是圈内人。行话会被解释，缩写会展开，未经证实的猜测会被明确标注。我们希望你看完之后，能和别人清楚地讲明白发生了什么。
                </p>
                <p>
                  目前站点设有六大栏目，覆盖明星动态、影视综艺、音乐现场、网红观察、时尚生活与八卦盘点，每周更新，不定期推出专题复盘。
                </p>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-6">
                {stats.map((item) => (
                  <div key={item.label} className="border-l-2 border-secondary-400 pl-4">
                    <p className="font-heading text-xl font-bold text-secondary-800 md:text-2xl">{item.value}</p>
                    <p className="mt-1 text-sm text-foreground-600">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="principles" className="scroll-mt-28 border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1400px] px-4 py-12 md:px-6 md:py-16">
            <div className="mb-8">
              <p className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.22em] text-primary-700">
                <span className="inline-block h-2 w-2 rounded-full bg-accent-500"></span>
                Principles
              </p>
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">内容原则</h2>
              <p className="mt-2 max-w-2xl text-sm text-foreground-600">
                这四条准则决定了我们跟进什么、怎么写，以及什么时候选择不写。
              </p>
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {principles.map((item) => (
                <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
                  <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-secondary-100">
                    <i className={`${item.icon} text-2xl text-secondary-700`} aria-hidden="true"></i>
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-700">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="voices" className="scroll-mt-28 border-b border-background-200 bg-background-50">
          <div className="mx-auto w-full max-w-[1400px] px-4 py-12 md:px-6 md:py-16">
            <div className="mb-8">
              <p className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.22em] text-primary-700">
                <span className="inline-block h-2 w-2 rounded-full bg-accent-500"></span>
                Voices
              </p>
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">读者怎么说</h2>
            </div>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              {testimonials.map((item) => (
                <figure key={item.name} className="flex h-full flex-col rounded-lg border border-background-200 bg-background-100 p-6">
                  <i className="ri-double-quotes-l text-2xl text-primary-300" aria-hidden="true"></i>
                  <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-800">
                    {item.text}
                  </blockquote>
                  <figcaption className="mt-5 flex items-center gap-3 border-t border-background-200 pt-4">
                    <span className="h-11 w-11 flex-shrink-0 overflow-hidden rounded-full">
                      <img
                        src={item.avatar}
                        alt={`${item.name} 的头像`}
                        title={`${item.name} · 瓜网读者`}
                        className="h-full w-full object-cover object-top"
                      />
                    </span>
                    <span>
                      <span className="block font-heading text-sm font-bold text-foreground-950">{item.name}</span>
                      <span className="block text-xs text-foreground-500">{item.role}</span>
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background-100">
          <div className="mx-auto grid w-full max-w-[1400px] grid-cols-1 gap-8 px-4 py-12 md:px-6 md:py-16 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">和我们聊聊</h2>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-700">
                无论是内容建议、线索提供还是合作咨询，都欢迎通过下面任意方式联系我们。编辑团队会在两个工作日内回复。
              </p>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <a
                  href={`mailto:${site.email}`}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-200"
                >
                  <i className="ri-mail-line text-primary-600" aria-hidden="true"></i>
                  {site.email}
                </a>
                <a
                  href={`mailto:${site.tipEmail}`}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-200"
                >
                  <i className="ri-megaphone-line text-accent-600" aria-hidden="true"></i>
                  {site.tipEmail}
                </a>
              </div>
            </div>
            <div className="flex items-end lg:justify-end">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                前往联系表单
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}